// Material sounds, synthesized with Web Audio (a port of the Nameplate
// mockup's engine). Nothing plays before the first user gesture creates the
// context, nothing plays when the player has sound off, and everything ducks
// while a justice's voice is playing so the Court is never masked.
import { sfxEnabled, subscribe } from './prefs';

interface Engine { ctx: AudioContext; out: GainNode; noise: AudioBuffer; bed: { src: AudioBufferSourceNode; g: GainNode } | null }
let A: Engine | null = null;
let duck = 1;
const MASTER = 0.55;

/** Create the context on a user gesture (idempotent). */
export function unlockSfx(): void {
  if (A || typeof window === 'undefined') return;
  const Ctor = (window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext);
  if (!Ctor) return;
  try {
    const ctx = new Ctor();
    const out = ctx.createGain();
    out.gain.value = MASTER;
    const comp = ctx.createDynamicsCompressor();
    comp.threshold.value = -18;
    out.connect(comp); comp.connect(ctx.destination);
    const len = Math.floor(ctx.sampleRate * 1.5);
    const noise = ctx.createBuffer(1, len, ctx.sampleRate);
    const d = noise.getChannelData(0);
    for (let i = 0; i < len; i += 1) d[i] = Math.random() * 2 - 1;
    A = { ctx, out, noise, bed: null };
  } catch { A = null; }
  applyTone();
}

if (typeof window !== 'undefined') {
  const first = () => { unlockSfx(); window.removeEventListener('pointerdown', first, true); window.removeEventListener('keydown', first, true); };
  window.addEventListener('pointerdown', first, true);
  window.addEventListener('keydown', first, true);
  // A hidden tab makes no sound and costs no audio thread.
  document.addEventListener('visibilitychange', () => {
    if (!A || A.ctx.state === 'closed') return;
    if (document.visibilityState === 'hidden') void A.ctx.suspend().catch(() => {});
    else void A.ctx.resume().catch(() => {});
  });
  subscribe(() => applyTone());
  // Every kit button answers its own press, on pointerdown (not click) so the sound lands with the pad going down.
  // Opt out with data-sfx="off"; the held mic key already has its own recording blips.
  window.addEventListener('pointerdown', (ev) => {
    if (ev.button !== 0 || !(ev.target instanceof Element)) return;
    const el = ev.target.closest('.pb');
    if (!el || (el as HTMLButtonElement).disabled || el.getAttribute('aria-disabled') === 'true') return;
    if (el.closest('[data-sfx="off"], .court-mic')) return;
    const m = (['wood', 'velvet', 'paper', 'marble', 'brass'] as const).find((c) => el.classList.contains(c));
    press(m ?? 'brass');
  }, true);
}

function on(): Engine | null {
  if (!A || !sfxEnabled() || A.ctx.state === 'closed') return null;
  // A hidden tab stays suspended: nothing a server event triggers there wakes it.
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return null;
  if (A.ctx.state === 'suspended') void A.ctx.resume().catch(() => {});
  return A;
}

/** While a justice speaks, effects drop to 40 percent. */
export function setDuck(speaking: boolean): void {
  duck = speaking ? 0.4 : 1;
  if (A) A.out.gain.setTargetAtTime(MASTER * duck, A.ctx.currentTime, 0.05);
}

function env(g: GainNode, t: number, a: number, peak: number, dcy: number) {
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(peak, t + a);
  g.gain.exponentialRampToValueAtTime(0.0001, t + a + dcy);
}

function noiseBurst({ dur = 0.02, type = 'highpass' as BiquadFilterType, f = 2400, q = 0.7, peak = 0.3, f2 = 0, at = 0 }) {
  const e = on(); if (!e) return;
  const t = e.ctx.currentTime + at;
  const src = e.ctx.createBufferSource(); src.buffer = e.noise;
  const fl = e.ctx.createBiquadFilter(); fl.type = type; fl.frequency.setValueAtTime(f, t); fl.Q.value = q;
  if (f2) fl.frequency.exponentialRampToValueAtTime(f2, t + dur);
  const g = e.ctx.createGain(); env(g, t, 0.002, peak, dur);
  src.connect(fl); fl.connect(g); g.connect(e.out); src.start(t); src.stop(t + dur + 0.05);
}

function tone({ f = 440, type = 'sine' as OscillatorType, dur = 0.2, peak = 0.2, a = 0.004, slide = 0, at = 0 }) {
  const e = on(); if (!e) return;
  const t = e.ctx.currentTime + at;
  const o = e.ctx.createOscillator(); o.type = type; o.frequency.setValueAtTime(f, t);
  if (slide) o.frequency.exponentialRampToValueAtTime(slide, t + dur);
  const g = e.ctx.createGain(); env(g, t, a, peak, dur);
  o.connect(g); g.connect(e.out); o.start(t); o.stop(t + a + dur + 0.05);
}

/** When a button last played its press sound, so a handler's own `SFX.click()` on the same press stays silent. */
let lastPressAt = -1e9;
const nowMs = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());
/** Each press lands a touch off the last (about 6 percent in pitch), so ten in a row do not repeat. */
const wobble = () => 1 + (Math.random() - 0.5) * 0.12;

export type PressMaterial = 'wood' | 'brass' | 'paper' | 'velvet' | 'marble';

/** The sound of a button going down, by what it is made of: wood is the lowest and heaviest, brass brighter and
 *  shorter, paper a soft dry tap. All under 40 ms and quiet next to a voice (the engine ducks them under speech). */
export function press(material: PressMaterial = 'brass'): void {
  lastPressAt = nowMs();
  const w = wobble();
  switch (material) {
    case 'wood':
      noiseBurst({ dur: 0.03, type: 'lowpass', f: 1500 * w, peak: 0.26 });
      tone({ f: 150 * w, dur: 0.045, peak: 0.12, slide: 95 * w });
      break;
    case 'paper':
      noiseBurst({ dur: 0.022, type: 'bandpass', f: 2700 * w, q: 0.9, peak: 0.12 });
      break;
    case 'velvet':
      noiseBurst({ dur: 0.035, type: 'lowpass', f: 800 * w, peak: 0.18 });
      break;
    case 'marble':
      noiseBurst({ dur: 0.016, type: 'highpass', f: 2800 * w, peak: 0.16 });
      tone({ f: 420 * w, dur: 0.03, peak: 0.05 });
      break;
    default:
      noiseBurst({ dur: 0.016, type: 'bandpass', f: 2300 * w, q: 2.2, peak: 0.2 });
      tone({ f: 330 * w, type: 'triangle', dur: 0.03, peak: 0.04 });
  }
}

/** `SFX.click` that a press sound within the last 400 ms has already answered. */
const pressAnswered = () => nowMs() - lastPressAt < 400;

export const SFX = {
  click() { if (!pressAnswered()) noiseBurst({ dur: 0.012, f: 3200, peak: 0.22 }); },
  tick() { noiseBurst({ dur: 0.008, f: 5200, peak: 0.12 }); },
  move() { noiseBurst({ dur: 0.018, f: 1800, type: 'bandpass', q: 3, peak: 0.25 }); tone({ f: 220, type: 'square', dur: 0.03, peak: 0.03 }); },
  cut() { noiseBurst({ dur: 0.11, type: 'bandpass', f: 3800, f2: 260, q: 1.4, peak: 0.5 }); tone({ f: 180, type: 'sawtooth', dur: 0.07, peak: 0.08, slide: 90 }); },
  knock() { tone({ f: 110, dur: 0.16, peak: 0.55, slide: 48 }); noiseBurst({ dur: 0.05, type: 'lowpass', f: 900, peak: 0.35 }); },
  gavel() { SFX.knock(); setTimeout(SFX.knock, 260); setTimeout(SFX.knock, 520); },
  lamp(red: boolean) { tone({ f: red ? 392 : 784, type: 'triangle', dur: 0.5, peak: 0.12 }); tone({ f: red ? 196 : 392, dur: 0.7, peak: 0.1 }); },
  chord() { [261.6, 329.6, 392, 523.3].forEach((f, i) => tone({ f, type: 'triangle', dur: 0.9, peak: 0.09, at: i * 0.012 })); },
  minor() { [220, 261.6, 329.6].forEach((f, i) => tone({ f, type: 'triangle', dur: 0.9, peak: 0.08, at: i * 0.012 })); },
  recOn() { tone({ f: 660, type: 'square', dur: 0.04, peak: 0.05 }); tone({ f: 990, type: 'square', dur: 0.04, peak: 0.05, at: 0.05 }); },
  recOff() { tone({ f: 990, type: 'square', dur: 0.04, peak: 0.05 }); tone({ f: 660, type: 'square', dur: 0.04, peak: 0.05, at: 0.05 }); },
  stamp() { noiseBurst({ dur: 0.04, type: 'lowpass', f: 1400, peak: 0.3 }); tone({ f: 140, dur: 0.08, peak: 0.2 }); },
  /** one justice's vote stamped on the slip: the same thump, a third higher when it goes counsel's way, lower when not */
  vote(withCounsel: boolean) {
    const w = wobble();
    noiseBurst({ dur: 0.04, type: 'lowpass', f: 1300 * w, peak: 0.2 });
    tone({ f: (withCounsel ? 196 : 147) * w, dur: 0.09, peak: 0.16, slide: (withCounsel ? 150 : 110) * w });
  },
  /** a nameplate lighting: a small brass ting */
  ting() { tone({ f: 1568, type: 'triangle', dur: 0.35, peak: 0.08 }); tone({ f: 2350, dur: 0.2, peak: 0.03, at: 0.01 }); },
  /** a coin dropping into the tally */
  coin() { tone({ f: 988, type: 'square', dur: 0.05, peak: 0.04 }); tone({ f: 1319, type: 'square', dur: 0.12, peak: 0.04, at: 0.06 }); },
  /** the lift's doors rolling shut on their track, and meeting */
  liftDoor() {
    // The doors close from 50 to 240 ms (lift.ts FLOORS.close) and meet at 240: the roll runs exactly that long and
    // the thud lands on the meeting, not 60 ms (four frames) after it (FEEL-05).
    noiseBurst({ dur: 0.19, type: 'lowpass', f: 700, f2: 240, q: 1.2, peak: 0.16, at: 0.05 });
    tone({ f: 70, dur: 0.19, peak: 0.08, slide: 55, at: 0.05 });
    tone({ f: 96, dur: 0.12, peak: 0.3, slide: 52, at: 0.24 });
  },
  /** the lift's arrival bell */
  ding() { tone({ f: 1318.5, type: 'triangle', dur: 1.1, peak: 0.1 }); tone({ f: 2637, dur: 0.5, peak: 0.025, at: 0.004 }); tone({ f: 3951, dur: 0.18, peak: 0.01 }); },
  /** a page turned or a paper set down */
  paper() { noiseBurst({ dur: 0.07, type: 'bandpass', f: 2600, f2: 900, q: 0.9, peak: 0.18 }); },
};

/** Quiet room tone outside the courtroom (never under an argument, which has its own
 *  near-silent bed in courtroom-audio.ts). Wanted by default; the Chamber turns it off. */
let toneWanted = true;
export function roomTone(onOff: boolean): void {
  toneWanted = onOff;
  applyTone();
}

function applyTone(): void {
  const e = toneWanted ? on() : null;
  if (!e) {
    if (A?.bed) { try { A.bed.src.stop(); } catch { /* already stopped */ } A.bed = null; }
    return;
  }
  if (e.bed) return;
  const src = e.ctx.createBufferSource(); src.buffer = e.noise; src.loop = true;
  const f = e.ctx.createBiquadFilter(); f.type = 'lowpass'; f.frequency.value = 320;
  const g = e.ctx.createGain(); g.gain.value = 0.018;
  src.connect(f); f.connect(g); g.connect(e.out); src.start();
  e.bed = { src, g };
}

/** Test hook. */
export function _sfxEngineForTests(): Engine | null { return A; }
